import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { ExpandMore, CheckCircle, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

declare global {
  interface Window {
    fbq: any;
  }
}

const SystemDesignMasterclassPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
    document.title = 'System Design Masterclass - Raymond Jones';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://hook.us2.make.com/jvqefq1pgp367pgpvenfbibb0v6olcxd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'system_design_masterclass',
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }

        navigate('/swe-thank-you');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const painPoints = [
    "You're staring at system design interview questions feeling completely lost, not knowing where to even start drawing boxes",
    "You've been rejected from multiple companies because you couldn't explain how to scale a distributed system beyond 'just add more servers'",
    "Every time an interviewer asks about load balancers, message queues, or database sharding, you freeze and give vague, theoretical answers",
    "You're watching your peers land $250k+ offers at FAANG while you're stuck explaining concepts you barely understand yourself",
    "You know you're smart enough to do the job, but you can't translate that into coherent system design answers that impress interviewers",
    "You've read articles and watched videos, but when interview pressure hits, your mind goes blank and you can't connect the dots",
    "You're tired of memorizing architectures without understanding WHY specific design decisions matter in real production systems"
  ];

  const benefits = [
    {
      pain: "Drawing boxes without understanding",
      benefit: "You'll confidently architect complete systems from scratch, explaining every component's purpose with clarity that makes interviewers take notes"
    },
    {
      pain: "Vague scaling answers",
      benefit: "You'll articulate precise scaling strategies using message queues, replicas, and sharding with the authority of someone who's built these systems"
    },
    {
      pain: "Freezing under pressure",
      benefit: "You'll calmly navigate any system design question because you've internalized the fundamental patterns that power every scalable application"
    },
    {
      pain: "Missing out on offers",
      benefit: "You'll walk into interviews with the same knowledge that landed engineers at Meta, Google, and Netflix their $280k+ compensation packages"
    },
    {
      pain: "Can't connect the dots",
      benefit: "You'll see the complete picture of how distributed systems work, connecting databases, caching, and APIs into production-ready architectures"
    }
  ];

  const deliverables = [
    "Complete Alex Xu System Design Interview Vol 1 & 2 breakdown with battle-tested solutions",
    "Designing Data-Intensive Applications (DDIA) deep-dive with practical applications",
    "11 real interview problems solved step-by-step (hotel reservations, message queues, payment systems)",
    "Location-based system design patterns (Uber, Google Maps, Snapchat architecture)",
    "Message queue implementation guide with Kafka, RabbitMQ strategies",
    "Metrics & monitoring system design (Datadog, InfluxDB patterns)",
    "Ad click aggregation with top-K algorithms and MapReduce",
    "Email service architecture with SMTP, IMAP protocol breakdowns",
    "S3 object storage system with replication and durability patterns",
    "Real-time gaming leaderboard using Redis sorted sets",
    "Payment system design with reconciliation and idempotency",
    "100+ hours of condensed knowledge in digestible video format"
  ];

  const faqs = [
    {
      question: "How is this different from other system design courses?",
      answer: "Most courses give you shallow theory. This masterclass is built from 100+ hours of studying the actual books (Alex Xu, DDIA, Stanley Chiang) that FAANG engineers recommend, condensed and explained by someone who's solved 1000+ LeetCode problems and passed real system design interviews. You're getting battle-tested knowledge, not generic templates."
    },
    {
      question: "I'm only preparing for mid-level roles. Is this overkill?",
      answer: "Absolutely not. This masterclass is specifically designed for mid-level system design interviews. We focus on the core concepts (message queues, databases, caching, load balancing) that 99% of mid-level interviews test. Senior-level complexity is clearly marked so you know exactly what's in scope."
    },
    {
      question: "How long will I have access to the content?",
      answer: "Lifetime access. Once you purchase, the content is yours forever, including any future updates. No recurring fees, no expiration dates. Learn at your own pace and revisit concepts whenever you need a refresher before interviews."
    },
    {
      question: "What if I don't have a strong CS background?",
      answer: "That's exactly why this exists. I break down complex concepts like quadtrees, geohashing, and MapReduce into understandable explanations. If you can code and have done some data structures work, you can follow this. I assume curiosity, not a PhD."
    },
    {
      question: "Can I really master system design from videos alone?",
      answer: "Videos are the foundation, but you need to practice. This masterclass gives you the frameworks and knowledge, but you'll need to do mock interviews (use resources like HelloInterview or peers) to solidify your skills. Think of this as your complete knowledge base that makes practice actually productive."
    },
    {
      question: "How quickly can I be interview-ready?",
      answer: "If you dedicate 2-3 hours daily, you can complete the core content in 2-3 weeks and be ready for mid-level interviews. Some students binge the content in 1 week during interview prep sprints. It depends on your pace, but the material is designed for rapid, focused learning."
    },
    {
      question: "Why is this $11 instead of free?",
      answer: "Two reasons: First, I spent 100+ hours studying and condensing this knowledge—that time has value. Second, people who pay attention are more committed. This small investment ensures you're serious about landing your next role. Plus, it's less than lunch at Chipotle."
    },
    {
      question: "What if this doesn't help me pass interviews?",
      answer: "If you go through this content and still can't explain how message queues prevent data loss, how to shard databases, or how to design a scalable payment system, I'll be shocked. But here's the thing: this gives you knowledge. Interview success also requires practice, communication skills, and luck with interviewer fit. This eliminates the knowledge gap—the rest is on you."
    },
    {
      question: "Is this content updated for 2025 interviews?",
      answer: "Yes. System design fundamentals don't change drastically year-to-year (databases, caching, load balancing are evergreen), but the examples and case studies reflect modern architecture patterns used at companies today. As new patterns emerge, content gets updated."
    },
    {
      question: "Can I get this free in the $8/month community?",
      answer: "Yes! If you're already in the community, you have access. This $11 one-time purchase is for people who want just this masterclass without committing to the monthly membership. Choose what works for your situation."
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Navigation />

      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="md">
          {/* Audience Callout */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'secondary.main',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              For software engineers preparing for mid-level interviews at FAANG and top-tier companies
            </Typography>
          </Box>

          {/* Bold Promise */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Master System Design Without Memorizing Templates
            </Typography>
          </Box>

          {/* What You Get */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.8,
              }}
            >
              Get instant access to 100+ hours of condensed system design knowledge from Alex Xu, DDIA, and Stanley Chiang's books,
              11 real interview problems with step-by-step solutions, complete architectures for distributed systems,
              message queues, databases, and payment systems, practical examples from Netflix, Uber, and Google,
              and the exact frameworks that helped engineers land $250k-$320k offers at Meta, Amazon, and Apple.
            </Typography>
          </Box>

          {/* CTA Button */}
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => {
                const emailSection = document.getElementById('email-signup');
                emailSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                color: 'black',
                py: 2.5,
                px: 6,
                fontWeight: 'bold',
                fontSize: '1.4rem',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                transition: 'transform 0.2s',
              }}
            >
              Get It For $11
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Or get it free in the $8/month community
            </Typography>
          </Box>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* Pain Points */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6, fontWeight: 'bold' }}
            >
              If Any Of This Sounds Like <span style={{ color: '#88C0D0' }}>You</span>
            </Typography>

            <Box sx={{ display: 'grid', gap: 3 }}>
              {painPoints.map((pain, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    border: '2px solid #BF616A',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Close sx={{ color: '#BF616A', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'text.primary', fontSize: '1.05rem', lineHeight: 1.7 }}>
                      {pain}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* Benefits */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6, fontWeight: 'bold' }}
            >
              Here's What Changes When You <span style={{ color: '#88C0D0' }}>Master This</span>
            </Typography>

            <Box sx={{ display: 'grid', gap: 4 }}>
              {benefits.map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '2px solid #A3BE8C',
                    '&:hover': {
                      border: '2px solid #88C0D0',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <CheckCircle sx={{ color: '#A3BE8C', mt: 0.5, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
                          Instead of: {item.pain}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.7 }}>
                          {item.benefit}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* Solution Introduction */}
          <Box sx={{ mb: 10, textAlign: 'center' }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4, fontWeight: 'bold' }}
            >
              Introducing: <span style={{ color: '#88C0D0' }}>The System Design Masterclass</span>
            </Typography>

            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', lineHeight: 1.8, maxWidth: '800px', mx: 'auto' }}
            >
              This is the complete system design education you've been searching for—distilled from the exact books
              and resources that FAANG engineers swear by, broken down into practical, interview-ready knowledge by
              someone who's been in your shoes and conquered 1000+ LeetCode problems and multiple system design interview rounds.
            </Typography>
          </Box>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* Discovery Story */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6, fontWeight: 'bold' }}
            >
              Why This <span style={{ color: '#88C0D0' }}>Exists</span>
            </Typography>

            <Paper sx={{ p: 5, bgcolor: 'rgba(136, 192, 208, 0.05)', border: '1px solid #434C5E' }}>
              <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.9, mb: 3 }}>
                I spent over <strong>100 hours</strong> reading through the books everyone tells you to read but nobody
                actually finishes: Alex Xu's System Design Interview Volume 1 & 2, Designing Data-Intensive Applications,
                and Stanley Chiang's System Design Interview guide. Not just skimming—deeply studying, taking notes,
                connecting patterns.
              </Typography>

              <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.9, mb: 3 }}>
                Here's what I discovered: these books contain <em>everything</em> you need to pass mid-level system design
                interviews, but they're dense, theoretical, and overwhelming when you're just trying to prepare for your
                Meta interview in 3 weeks. Nobody has 100 hours to dedicate to reading before interview season hits.
              </Typography>

              <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.9, mb: 3 }}>
                So I did something nobody else has done: I broke down every single chapter, extracted the practical
                interview-ready knowledge, removed the academic fluff, and organized it into the exact format interviewers
                expect. I mapped out which concepts matter for mid-level vs senior roles. I identified the patterns that
                show up in 90% of interviews.
              </Typography>

              <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.9, mb: 3 }}>
                This isn't just another system design course with generic templates. This is <strong>100 hours of elite
                engineering knowledge compressed into an actionable learning path</strong> by someone who solved 1000+
                algorithm problems and understands how to actually pass technical interviews. Someone who reads the boring
                books so you don't have to waste months doing it yourself.
              </Typography>

              <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', lineHeight: 1.9 }}>
                You could spend the next 3 months reading these books yourself. Or you could get the concentrated,
                interview-focused version <strong>right now</strong> and start practicing what actually matters. That's
                why this masterclass exists—to give you the unfair advantage of having someone who's already done the hard
                work translate it into the language of modern tech interviews.
              </Typography>
            </Paper>
          </Box>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* What's Included */}
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6, fontWeight: 'bold' }}
            >
              Everything <span style={{ color: '#88C0D0' }}>Included</span>
            </Typography>

            <Box sx={{ display: 'grid', gap: 2 }}>
              {deliverables.map((item, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    border: '2px solid #434C5E',
                    '&:hover': {
                      border: '2px solid #88C0D0',
                      transform: 'translateX(4px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <CheckCircle sx={{ color: '#88C0D0', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'text.primary', fontSize: '1.05rem', lineHeight: 1.7 }}>
                      {item}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 'bold', mb: 2 }}>
                Total Value: 100+ Hours of Engineering Knowledge
              </Typography>
              <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                Your Investment: Just $11
              </Typography>
            </Box>
          </Box>

          {/* Email Signup */}
          <Paper
            id="email-signup"
            sx={{
              p: 6,
              mb: 10,
              bgcolor: 'rgba(136, 192, 208, 0.1)',
              border: '3px solid #88C0D0',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'secondary.main',
                mb: 4,
                fontWeight: 'bold',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              Ready to Master System Design?
            </Typography>

            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Get Instant <span style={{ color: '#88C0D0' }}>Access</span>
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ maxWidth: 400, mx: 'auto' }}
            >
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                variant="outlined"
                disabled={isSubmitting}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.default',
                    '& fieldset': {
                      borderColor: '#333',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#88C0D0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#88C0D0',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '1.1rem',
                    py: 2,
                  },
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  color: 'black',
                  py: 2.5,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'black' }} />
                    Processing...
                  </Box>
                ) : (
                  'Get Access For $11'
                )}
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
              One-time payment. Lifetime access. No subscriptions.
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Or join the $8/month community to get this + all other courses
            </Typography>
          </Paper>

          <Divider sx={{ my: 8, borderColor: '#434C5E' }} />

          {/* FAQ */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 6, fontWeight: 'bold' }}
            >
              Frequently Asked <span style={{ color: '#88C0D0' }}>Questions</span>
            </Typography>

            <Box>
              {faqs.map((faq, index) => (
                <Accordion
                  key={index}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid #434C5E',
                    mb: 2,
                    '&:before': {
                      display: 'none',
                    },
                    '&:hover': {
                      borderColor: '#88C0D0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: 'secondary.main' }} />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        my: 2,
                      },
                    }}
                  >
                    <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1.1rem' }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          {/* Final CTA */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Paper
              sx={{
                p: 6,
                bgcolor: 'rgba(136, 192, 208, 0.1)',
                border: '3px solid #88C0D0',
                borderRadius: 4,
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
              >
                Don't Waste Another Month Feeling <span style={{ color: '#BF616A' }}>Lost</span>
              </Typography>

              <Typography
                variant="h6"
                sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.8 }}
              >
                Your next interview could be in 2 weeks. Get the knowledge that took me 100+ hours to extract,
                organized exactly how you need it to succeed.
              </Typography>

              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  const emailSection = document.getElementById('email-signup');
                  emailSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  color: 'black',
                  py: 2.5,
                  px: 6,
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'transform 0.2s',
                }}
              >
                Get Instant Access for $11
              </Button>

              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, fontStyle: 'italic' }}>
                Limited spots available to maintain quality. Offer valid while enrollment is open.
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SystemDesignMasterclassPage;
